import React, { useState } from 'react';
// MUI Imports for styling messages similar to ChatInterface
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';

// Simplified component focusing only on displaying the transcript
// Removed simulationId, existingReview, and review fetching logic
const Gen2TraceInline = ({ trace }) => {
  const [expanded, setExpanded] = useState(false);
  if (!trace) return null;

  const modeColors = {
    fast: { bg: '#e0e0e0', color: '#424242' },
    deep_thinking: { bg: '#bbdefb', color: '#1565c0' },
    consultation: { bg: '#e1bee7', color: '#7b1fa2' }
  };
  const style = modeColors[trace.processingMode] || modeColors.fast;

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
      >
        <Chip
          label={trace.processingMode?.replace('_', ' ') || 'unknown'}
          size="small"
          sx={{ backgroundColor: style.bg, color: style.color, fontWeight: 600, fontSize: '10px', textTransform: 'capitalize', height: 20 }}
        />
        {trace.latencyMs != null && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
            {(trace.latencyMs / 1000).toFixed(1)}s
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px', ml: 'auto' }}>
          {expanded ? '▲' : '▼'}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid rgba(0,0,0,0.1)', fontSize: '11px' }}>
          {trace.triggerReason && (
            <Typography variant="caption" display="block">Trigger: {trace.triggerReason}</Typography>
          )}
          {trace.reasoningOutput?.clinicalAnalysis && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
              <strong>Analysis:</strong> {trace.reasoningOutput.clinicalAnalysis}
            </Typography>
          )}
          {trace.reasoningOutput?.responsePlan && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
              <strong>Plan:</strong> {trace.reasoningOutput.responsePlan}
            </Typography>
          )}
          {trace.consultationData && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              <strong>Consulted:</strong> {trace.consultationData.moduleKey}
              {trace.consultationData.wasApplied ? ' (applied)' : ' (not applied)'}
            </Typography>
          )}
          {trace.formulationSnapshot?.wasUpdatedThisTurn && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Formulation updated to v{trace.formulationSnapshot.version}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const TranscriptDisplay = ({ transcript }) => {

  if (!Array.isArray(transcript) || transcript.length === 0) {
    return <Typography sx={{ p: 2, color: 'text.secondary' }}>No transcript messages to display or transcript is not in the correct array format.</Typography>;
  }

  // Container style using MUI Box for consistency
  const transcriptContainerStyle = {
    mt: 2, // Margin top
    border: '1px solid',
    borderColor: 'divider',
    padding: 1, // Reduced padding
    maxHeight: '500px', // Increased height slightly
    overflowY: 'auto',
    backgroundColor: 'grey.50', // Light background
  };

  return (
    <Box sx={transcriptContainerStyle}>
      {/* Use MUI List for better structure */}
      <List disablePadding>
        {transcript.map((msg, index) => {
          // Determine sender role based on msg.sender
          const isClient = msg.sender === 'client';
          const isTherapistAI = msg.sender === 'therapist'; // AI Therapist in simulations
          const isSystem = msg.sender === 'system';
          // Add isHumanTherapist if 'human_therapist' is a possible value in msg.sender for other contexts
          // For now, focusing on simulation transcript structure: 'client', 'therapist', 'system'

          let justifyContent;
          let bgcolor;
          let color;
          let borderRadius;
          let label = null;

          if (isClient) {
            justifyContent = 'flex-end';
            bgcolor = 'primary.main';
            color = 'primary.contrastText';
            borderRadius = '15px 15px 0 15px';
            label = 'Client';
          } else if (isTherapistAI) {
            justifyContent = 'flex-start';
            bgcolor = 'grey.200'; // Distinct color for AI therapist
            color = 'text.primary';
            borderRadius = '15px 15px 15px 0';
            label = 'AI Therapist';
          } else if (isSystem) {
            justifyContent = 'center';
            bgcolor = 'transparent';
            color = 'text.secondary';
            // No border radius for system messages, or specific styling
          } else { // Fallback for unknown senders
            justifyContent = 'flex-start';
            bgcolor = 'grey.100';
            color = 'text.primary';
            borderRadius = '15px';
            label = msg.sender || 'Unknown';
          }

          return (
            <ListItem key={msg._id || index} sx={{ display: 'flex', justifyContent, px: 1, py: 0.5 }}>
              {isSystem ? (
                 <Typography variant="caption" sx={{ color: color, fontStyle: 'italic', textAlign: 'center', width: '100%' }}>
                   {msg.text}
                 </Typography>
              ) : (
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    borderRadius: borderRadius,
                    bgcolor: bgcolor,
                    color: color,
                    maxWidth: '80%',
                    wordWrap: 'break-word',
                  }}
                >
                  <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    {label}
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {msg.text}
                  </Typography>
                   {msg.timestamp && (
                       <Typography variant="caption" display="block" sx={{ mt: 0.5, textAlign: isClient ? 'right' : 'left', fontSize: '0.7rem', opacity: 0.7 }}>
                           {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                       </Typography>
                   )}
                   {msg.gen2Trace && <Gen2TraceInline trace={msg.gen2Trace} />}
                </Paper>
              )}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default TranscriptDisplay;
